import PincodeField, {pincodeLocation,emptyPincodeLocation} from '@/components/PincodeField';
/**
 * VendorSettings.jsx — Iter 50.
 *
 * Two tabs:
 *   - Profile: name, tagline, contact email, website, sector tags (PATCH /vendor/me).
 *   - Password: change password (POST /auth/change-password).
 */
import React, { useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { FloppyDisk, Lock, User, Star } from '@phosphor-icons/react';
import { formatDate } from '@/utils/datetime';
import LogoUploader from '../../components/LogoUploader';

const VendorSettings = ({ vendor, accent, user, onReload }) => {
  const [tab, setTab] = useState('profile');
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1280px] mx-auto" data-testid="vendor-settings-screen">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: accent }}>Account</div>
        <h1 className="text-3xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>Settings</h1>
        <p className="text-sm text-text-secondary">Edit how founders see you on the marketplace and rotate your password.</p>
      </div>

      <div className="border-b border-border mb-6">
        <div className="flex items-center gap-1">
          <TabBtn active={tab === 'profile'} accent={accent} onClick={() => setTab('profile')} icon={User} testid="vendor-settings-tab-profile">Profile</TabBtn>
          <TabBtn active={tab === 'password'} accent={accent} onClick={() => setTab('password')} icon={Lock} testid="vendor-settings-tab-password">Password</TabBtn>
        </div>
      </div>

      {tab === 'profile' ? (
        <ProfileTab vendor={vendor} accent={accent} onReload={onReload} />
      ) : (
        <PasswordTab accent={accent} />
      )}
    </div>
  );
};

const TabBtn = ({ active, accent, onClick, icon: I, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`relative px-4 py-3 text-sm font-medium inline-flex items-center gap-1.5 ${active ? 'text-text-primary' : 'text-text-muted hover:text-text-secondary'}`}>
    <I size={13} weight="bold" /> {children}
    {active && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: accent }} />}
  </button>
);

const ProfileTab = ({ vendor, accent, onReload }) => {
  const [form, setForm] = useState({
    name: vendor.name || '',
    tagline: vendor.tagline || '',
    contact_email: vendor.contact_email || '',
    website: vendor.website || '',
    address: vendor.address || '',street_address:vendor.street_address||vendor.address||'',pincode:vendor.pincode||'',hq_city:vendor.hq_city||'',hq_state:vendor.hq_state||'',hq_country:vendor.hq_country||'',hq_location:vendor.hq_location||'',
    gst_number: vendor.gst_number || '',
  });
  const [busy, setBusy] = useState(false);
  const [locationReady,setLocationReady]=useState(false);

  const save = async () => {
    if((form.pincode||form.hq_city!==(vendor.hq_city||'')||form.pincode!==(vendor.pincode||''))&&!locationReady){toast.error('Wait for a valid pincode location before saving');return;}
    setBusy(true);
    try {
      await api.patch('/vendor/me', form);
      toast.success('Profile updated');
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <>
      <div className="bg-surface border border-border rounded-2xl p-5 max-w-xl" data-testid="vendor-settings-profile">
        <div className="mb-4">
          <LogoUploader
            logoFileId={vendor.logo_file_id}
            accent={accent}
            testidPrefix="vendor-logo"
            onUploaded={async (fileId) => {
              // (Iter 51) Persist logo immediately so the uploader is decoupled
              // from the rest of the profile form's "Save changes" cycle.
              await api.patch('/vendor/me', { logo_file_id: fileId });
              onReload();
            }}
          />
        </div>
        <div className="space-y-3">
          <Inp label="Vendor name *" v={form.name} on={(v) => setForm((p) => ({ ...p, name: v }))} testid="vendor-settings-name" />
          <Inp label="Tagline" v={form.tagline} on={(v) => setForm((p) => ({ ...p, tagline: v }))} testid="vendor-settings-tagline" />
          <Inp label="Contact email" v={form.contact_email} on={(v) => setForm((p) => ({ ...p, contact_email: v }))} testid="vendor-settings-email" />
          <Inp label="Website" v={form.website} on={(v) => setForm((p) => ({ ...p, website: v }))} testid="vendor-settings-website" />
          <div>
            <PincodeField label="Business pincode" value={form.pincode} location={form.hq_location||vendor.address} onChange={pin=>setForm(current=>({...current,...emptyPincodeLocation(pin)}))} onResolve={location=>setForm(current=>({...current,...pincodeLocation(location)}))} onStatusChange={setLocationReady} disabled={busy} testId="vendor-settings-pincode"/><label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5 mt-4">Street / building details (for invoices)</label>
            <textarea value={form.street_address} onChange={(e) => setForm((p) => ({ ...p, street_address: e.target.value, address:e.target.value }))}
              data-testid="vendor-settings-address" rows={3} placeholder="Building, street or office number. City and state come from your pincode."
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
          </div>
          <Inp label="GST number (GSTIN)" v={form.gst_number} on={(v) => setForm((p) => ({ ...p, gst_number: v.toUpperCase() }))} testid="vendor-settings-gst" />
        </div>
        <button onClick={save} disabled={busy} data-testid="vendor-settings-save"
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accent }}>
          <FloppyDisk size={11} weight="bold" /> {busy ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      <VendorReviews accent={accent} />
    </>
  );
};

/* (Iter 68.2) The ratings founders left after approving a completed request. */
const VendorReviews = ({ accent }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/vendor/me/reviews');
        setData(r.data);
      } catch (_) { /* non-fatal */ }
    })();
  }, []);
  const reviews = data?.reviews || [];
  return (
    <div className="mt-5 bg-surface border border-border rounded-2xl p-5 max-w-3xl" data-testid="vendor-reviews">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Star size={15} weight="fill" style={{ color: '#f59e0b' }} />
          <h2 className="font-heading font-bold text-lg text-text-primary">Customer reviews</h2>
        </div>
        {data && data.count > 0 && (
          <div className="text-xs text-text-muted" data-testid="vendor-reviews-summary">
            <span className="font-mono font-bold text-text-primary">{Number(data.rating_avg).toFixed(1)}</span> / 5
            {' · '}{data.count} review{data.count === 1 ? '' : 's'}
          </div>
        )}
      </div>
      {!data ? (
        <p className="text-sm text-text-muted">Loading…</p>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-text-muted" data-testid="vendor-reviews-empty">
          No reviews yet. Founders can rate you after they approve a completed service request.
        </p>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} className="bg-background border border-border rounded-lg p-3" data-testid={`vendor-review-${r.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n} size={12} weight={n <= r.rating ? 'fill' : 'regular'}
                      style={{ color: n <= r.rating ? '#f59e0b' : 'var(--text-muted)' }} />
                  ))}
                </div>
                <div className="text-[10px] text-text-muted">
                  {r.founder_startup_name || 'Founder'}{r.created_at ? ` \u00b7 ${formatDate(r.created_at)}` : ''}
                </div>
              </div>
              {r.catalog_item_name && <div className="text-[10px] text-text-muted mb-1">{r.catalog_item_name}</div>}
              {r.comment && <p className="text-[12px] text-text-secondary leading-relaxed">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PasswordTab = ({ accent }) => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (form.new_password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (form.new_password !== form.confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: form.current_password, new_password: form.new_password });
      toast.success('Password updated');
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-surface border border-border rounded-2xl p-5 max-w-md" data-testid="vendor-settings-password">
      <div className="space-y-3">
        <Inp label="Current password *" type="password" v={form.current_password} on={(v) => setForm((p) => ({ ...p, current_password: v }))} testid="vendor-settings-current-password" />
        <Inp label="New password *" type="password" v={form.new_password} on={(v) => setForm((p) => ({ ...p, new_password: v }))} testid="vendor-settings-new-password" />
        <Inp label="Confirm new password *" type="password" v={form.confirm} on={(v) => setForm((p) => ({ ...p, confirm: v }))} testid="vendor-settings-confirm-password" />
      </div>
      <button onClick={save} disabled={busy} data-testid="vendor-settings-change-password"
        className="mt-4 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: accent }}>
        <Lock size={11} weight="bold" /> {busy ? 'Updating…' : 'Update password'}
      </button>
    </div>
  );
};

const Inp = ({ label, v, on, type = 'text', testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <input type={type} value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
      className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm" />
  </div>
);

export default VendorSettings;
